export const OKVItemType = {
  Product: 'product',
  Category: 'category',
  Series: 'series',
} as const;

type CategoryImage = {
  key: string;
  orientation: string;
  sequence: number;
  thumbnail: string;
};

type SeriesImage = CategoryImage & {
  small?: string;
  medium?: string;
  large?: string;
};

type OKVCategoryImage =
  | {
      categoryType: typeof OKVItemType.Series;
      images: SeriesImage[];
    }
  | {
      categoryType: typeof OKVItemType.Category | typeof OKVItemType.Product;
      images: CategoryImage[];
    };

type Overview = {
  name: string;
  url: string;
};

type Ancestor = {
  id: string;
  name: string;
  uid: string;
};

export type OKVCategory = OKVCategoryImage & {
  id: string;
  uuid: string;
  name: string;
  hasProducts: boolean;
  hasSpareParts: boolean;
  orderHint: string;
  ancestors: Ancestor[];
  subCategories?: OKVCategory[];
  total?: number;
  slogan?: string;
  description?: string;
  overviews?: Overview[];
};

export type OKVProduct = {
  key: string;
  categoryId: string;
  pictureObject: PictureObject;
  pictureObjectMobile: PictureObjectMobile;
  name: string;
  state: ProductStateProps;
  description: string;
  numberArticles: number;
  articles: {
    articlesListSize: number;
  };
  title: string;
  subtitle: string;
  categories: OKVCategory[];
  deprecated?: boolean;
  gallery?: {
    list?: any[];
  };
};

type ProductStateProps = {
  bimReady: boolean;
  bimRelevant: boolean;
  buyable: boolean;
  from: unknown;
  indicator: string | null;
  orderRelated: boolean;
  replacedBy: unknown;
  replacementFor: unknown;
};

export type CategoryTreeRoot = {
  id: string;
  name: string;
  subCategories: OKVCategory[];
  hasProducts: boolean;
  hasSpareParts: boolean;
};

export type OKVItem = {
  identifier: string;
  catalogLocale: string;
  type: (typeof OKVItemType)[keyof typeof OKVItemType];
  product?: OKVProduct;
};

export type OKVJsonContent<T extends OKVItem = OKVItem> = {
  items: T[];
  title?: string;
};

export type OKVTile = OKVItem & {
  catalogLanguage: string;
  filterProductThumbnails: () => null;
  product: OKVProduct;
  total: number;
};

export type BackgroundColor = 'white' | 'porcelain' | 'medium-grey';

export type CatalogWrapperProps = {
  backgroundColor: BackgroundColor;
  isAccordion: boolean;
};
export type AccordionWrapperProps = { backgroundColor: BackgroundColor; isDesktop: boolean };
export type OkvTilesWrapperProps = { backgroundColor: BackgroundColor; isCarousel: boolean };
export type OkvTileWrapperProps = {
  backgroundColor: BackgroundColor;
  type: string;
  isAccordion?: boolean;
};

export type Props = {
  title: string;
  anchor: string;
  subtitle: string;
  contentIndex: number;
  titleInAnchor: boolean;
  catalogName: string;
  isSectionReference: boolean;
  data: OKVJsonContent[];
  backgroundColor: BackgroundColor;
  hideCount: boolean;
  carousel: boolean;
  text?: string;
  serverProps: {
    data: OKVJsonContent[];
  };
};

export type AccordionViewProps = {
  items: OKVJsonContent[];
  carousel: boolean;
  backgroundColor: BackgroundColor;
};

export type OkvTilesProps = {
  items: (OKVTile | OKVItem)[];
  carousel: boolean;
  backgroundColor: BackgroundColor;
  size: (4 | 6 | 8 | 12)[];
  isAccordion?: boolean;
};

export type OkvTileProps = {
  backgroundColor: BackgroundColor;
  catalogLocale: string;
  filterProductThumbnails: () => void;
  product: OKVProduct;
  total: number;
  type: string;
  identifier?: string;
  isAccordion?: boolean;
};
